import { useAuth } from '@/lib'
import { logger } from '@/ui/utilities/instrumentation/logger'
import { useChat } from '@ai-sdk/react'
import { axiosInstance, conversationIndexQueryKey, conversationShowQueryKey } from '@jobilla/nexus-sdk'
import type { paths } from '@jobilla/nexus-sdk/paths'
import { useQueryClient } from '@tanstack/react-query'
import { DefaultChatTransport } from 'ai'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useConversationHistory } from './use-conversation-history'
import type { ChatFocus } from './use-current-focus'

type SendMessagePath = keyof Pick<paths, '/ai/conversations/{conversationId}/messages'>
const SEND_MESSAGE_PATH: SendMessagePath = '/ai/conversations/{conversationId}/messages'

function buildSendUrl(conversationId: string): string {
	return `${axiosInstance.defaults.baseURL}${SEND_MESSAGE_PATH.replace('{conversationId}', conversationId)}`
}

export function useGlobalChat(conversationId: string, isNew = false, focus: ChatFocus | null = null) {
	const { getAccessToken } = useAuth()
	const queryClient = useQueryClient()
	const [errorMessage, setErrorMessage] = useState<string | null>(null)

	const { messages: initialMessages, isLoadingHistory } = useConversationHistory(conversationId, isNew)

	const focusRef = useRef(focus)
	focusRef.current = focus

	const hasSyncedRef = useRef(false)

	useEffect(() => {
		hasSyncedRef.current = false
	}, [conversationId])

	const { messages, status, sendMessage: originalSend, stop, setMessages } = useChat({
		transport: new DefaultChatTransport({
			api: buildSendUrl(conversationId),
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json',
			},
		}),
		id: conversationId,
		sendAutomaticallyWhen: () => false,
		onError: (error: Error) => {
			setErrorMessage('Something went wrong. Please try again.')
			logger.error(error, { data: { scope: 'GlobalChat' } })
		},
	})

	useEffect(() => {
		if (hasSyncedRef.current || isLoadingHistory) {
			return
		}
		setMessages(initialMessages)
		hasSyncedRef.current = true
	}, [isLoadingHistory, initialMessages, setMessages])

	const sendMessage = useCallback(async (text: string) => {
		if (!text.trim()) {
			return
		}
		setErrorMessage(null)
		try {
			const token = await getAccessToken()
			await originalSend(
				{ text },
				{
					headers: {
						Authorization: `Bearer ${token}`,
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					// Authoritative page context: explicit nulls clear the focus.
					// The endpoint speaks the AI SDK wire format rather than an input
					// DTO, so nothing checks these key names — they must match the
					// `entityType`/`entityId` the controller reads. The Focus type
					// itself is frontend-only and still snake_case.
					body: {
						entityType: focusRef.current?.entity_type ?? null,
						entityId: focusRef.current?.entity_id ?? null,
					},
				},
			)
		} catch (err) {
			setErrorMessage('Something went wrong. Please try again.')
			logger.error(err, { data: { scope: 'GlobalChat' } })
		} finally {
			// Reset sync guard so history re-fetch populates full tool output
			// (part.output) which the stream protocol doesn't carry.
			hasSyncedRef.current = false
			void queryClient.invalidateQueries({ queryKey: conversationIndexQueryKey() })
			void queryClient.invalidateQueries({ queryKey: conversationShowQueryKey(conversationId) })
		}
	}, [conversationId, getAccessToken, originalSend, queryClient])

	const isLoading = status === 'submitted'
		|| (status === 'streaming'
			&& (messages.at(-1)?.role !== 'assistant'
				|| messages.at(-1)?.parts.length === 0
				|| messages.at(-1)?.parts.at(-1)?.type === 'step-start'))

	return {
		messages,
		sendMessage,
		stop,
		status,
		isProcessing: status === 'submitted' || status === 'streaming',
		isLoading,
		isLoadingHistory,
		errorMessage,
		setMessages,
	}
}
